// FinKloz — Bank Rec Account Tab
// Single account reconciliation workflow: Upload -> Reconcile -> Review.

import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { sans, mono, green, greenLight, greenDark, red, amber, amberLight, useTheme } from '../tokens';
import BankRecUploadZone from './BankRecUploadZone';
import BankRecMatchView from './BankRecMatchView';
import BankRecDiscrepancies from './BankRecDiscrepancies';
import { parseBankStatement, BANK_ACCOUNTS } from '../services/bankStatementParser';
import { parseGLExport, parseGLWithMapping, filterFXReversals } from '../services/glDataParser';
import { matchTransactions } from '../services/matchingEngine';
import { classifyReconcilingItems, computeReconciliation, generateProposedJEs, computeFXConversion } from '../services/reconciliationEngine';

const teal = '#0D9488';
const tealLight = '#CCFBF1';

function StepIndicator({ steps, currentStep, t }) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 16, alignItems: 'center' }}>
      {steps.map((step, i) => {
        const isActive = i === currentStep;
        const isDone = i < currentStep;
        return (
          <React.Fragment key={i}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '3px 8px', borderRadius: 4,
              background: isDone ? greenLight : isActive ? tealLight : t.pillBg,
              border: `1px solid ${isDone ? green : isActive ? teal : t.border}`,
            }}>
              <span style={{
                fontFamily: mono, fontSize: 10, fontWeight: 700,
                color: isDone ? green : isActive ? teal : t.textMuted,
              }}>
                {isDone ? '\u2713' : i + 1}
              </span>
              <span style={{
                fontFamily: sans, fontSize: 10, fontWeight: isActive ? 600 : 400,
                color: isDone ? green : isActive ? teal : t.textMuted,
              }}>
                {step}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ width: 12, height: 1, background: t.border }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// Get GL ending balance from TB import for this account
function getGLBalanceFromTB(accountNo) {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('finkloz_gl_mappings_')) {
        const data = JSON.parse(localStorage.getItem(key));
        if (data?.accounts) {
          const acct = data.accounts.find(a => String(a.code) === String(accountNo));
          if (acct) return acct.balance || 0;
        }
      }
    }
  } catch { /* ignore */ }
  return null;
}

export default function BankRecAccountTab({ accountNo, accountData, dispatch, accountConfig, glAccounts, companyId, month, saveNow }) {
  const t = useTheme();
  const isUSD = accountConfig?.currency === 'USD';
  const [reconciling, setReconciling] = useState(false);
  const [fxRate, setFxRate] = useState(accountData?.fxConversion?.fxRate || null);
  const [glMappingNeeded, setGlMappingNeeded] = useState(null); // { preview, _rows }
  const [glMapping, setGlMapping] = useState({ date: -1, description: -1, debit: -1, credit: -1, balance: -1, txnAmount: -1, journal: -1 });
  const [glHeaderRow, setGlHeaderRow] = useState(0);
  const [fxRateDate, setFxRateDate] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);

  // Auto-fetch month-end FX rate from Bank of Canada
  useEffect(() => {
    if (!isUSD || fxRate) return;
    const fetchRate = async () => {
      setFxLoading(true);
      try {
        // Use the month prop to get the correct month-end rate
        // month format: "2026-01" -> need rate for Jan 31
        const [year, mon] = month.split('-');
        const lastDay = new Date(parseInt(year), parseInt(mon), 0); // last day of month (month is 1-indexed, Date uses 0-indexed so this gives last day)
        const startDate = new Date(lastDay);
        startDate.setDate(startDate.getDate() - 5); // 5 days before to catch weekends
        const from = startDate.toISOString().split('T')[0];
        const to = lastDay.toISOString().split('T')[0];

        const resp = await fetch(
          `https://www.bankofcanada.ca/valet/observations/FXUSDCAD/json?start_date=${from}&end_date=${to}`
        );
        if (resp.ok) {
          const data = await resp.json();
          const obs = data.observations || [];
          if (obs.length > 0) {
            const last = obs[obs.length - 1];
            const rate = parseFloat(last.FXUSDCAD.v);
            setFxRate(rate);
            setFxRateDate(last.d);
          }
        }
      } catch (e) {
        void('Failed to fetch BoC FX rate:', e.message);
      } finally {
        setFxLoading(false);
      }
    };
    fetchRate();
  }, [isUSD, month]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live reconciliation — recomputed from current match state
  const liveReconciliation = useMemo(() => {
    if (!accountData?.matchResult || !accountData?.statement || !accountData?.glData) return accountData?.reconciliation;
    const mr = accountData.matchResult;

    // For USD accounts: if all transactions matched, it's reconciled (FX is separate)
    if (isUSD) {
      const allMatched = (mr.unmatchedBank || []).length === 0 && (mr.unmatchedGL || []).length === 0;
      const bankBal = accountData.statement.metadata.closingBalance || 0;
      return {
        bankBalance: bankBal,
        outstandingChecks: 0,
        depositsInTransit: 0,
        adjustedBankBalance: bankBal,
        glBalance: bankBal, // USD transactions matched = same balance
        bankFees: 0,
        interest: 0,
        otherAdjustments: 0,
        adjustedBookBalance: bankBal,
        difference: allMatched ? 0 : null,
        isReconciled: allMatched,
      };
    }

    const bankBal = accountData.statement.metadata.closingBalance || 0;
    const glBal = accountData.glData.endingBalance || 0;
    const liveItems = classifyReconcilingItems(mr.unmatchedBank || [], mr.unmatchedGL || []);
    return computeReconciliation(bankBal, glBal, liveItems);
  }, [accountData?.matchResult, accountData?.statement, accountData?.glData, accountData?.reconciliation, isUSD]);

  const liveReconcilingItems = useMemo(() => {
    if (!accountData?.matchResult) return accountData?.reconcilingItems || [];
    return classifyReconcilingItems(accountData.matchResult.unmatchedBank || [], accountData.matchResult.unmatchedGL || []);
  }, [accountData?.matchResult, accountData?.reconcilingItems]);

  const status = accountData?.status || 'not_started';

  const steps = ['Upload Statement', 'Upload GL', 'Reconcile', 'Review'];
  const stepIdx = status === 'not_started' ? 0
    : status === 'statement_uploaded' ? 1
    : status === 'gl_uploaded' ? 0
    : status === 'ready_to_reconcile' ? 2
    : status === 'matched' ? 3
    : status === 'reviewed' || status === 'completed' ? 4
    : 2;

  // Handle bank statement upload
  const handleStatementUpload = useCallback(async (file) => {
    const result = await parseBankStatement(file);
    dispatch({
      type: 'STATEMENT_UPLOADED',
      accountNo,
      statement: {
        file: { name: file.name, size: file.size, uploadedAt: new Date().toISOString() },
        metadata: result.metadata,
        transactions: result.transactions,
      },
    });
  }, [dispatch, accountNo]);

  // Handle GL export upload
  const handleGLUpload = useCallback(async (file) => {
    try {
      const result = await parseGLExport(file);
      setGlMappingNeeded(null);
      dispatch({
        type: 'GL_UPLOADED',
        accountNo,
        glData: {
          source: 'csv',
          file: { name: file.name, size: file.size, uploadedAt: new Date().toISOString() },
          entries: result.entries,
          totalDebit: result.totalDebit,
          totalCredit: result.totalCredit,
          endingBalance: result.endingBalance,
          openingBalance: 0,
        },
      });
    } catch (err) {
      if (err.needsMapping) {
        setGlMappingNeeded({ preview: err.preview, _rows: err._rows, fileName: file.name, fileSize: file.size });
      } else {
        throw err;
      }
    }
  }, [dispatch, accountNo]);

  // Apply manual column mapping
  const handleApplyMapping = useCallback(() => {
    if (!glMappingNeeded?._rows || glMapping.date === -1) return;
    const result = parseGLWithMapping(glMappingNeeded._rows, glMapping, glHeaderRow);
    setGlMappingNeeded(null);
    dispatch({
      type: 'GL_UPLOADED',
      accountNo,
      glData: {
        source: 'csv',
        file: { name: glMappingNeeded.fileName, size: glMappingNeeded.fileSize, uploadedAt: new Date().toISOString() },
        entries: result.entries,
        totalDebit: result.totalDebit,
        totalCredit: result.totalCredit,
        endingBalance: result.endingBalance,
        openingBalance: 0,
      },
    });
  }, [glMappingNeeded, glMapping, glHeaderRow, dispatch, accountNo]);

  // Run reconciliation
  const handleReconcile = useCallback(() => {
    if (!accountData?.statement || !accountData?.glData) return;
    setReconciling(true);

    // Use setTimeout to keep UI responsive
    setTimeout(() => {
      try {
        // Preserve ALL existing matches — only re-match new/unmatched entries
        const existingMatches = accountData.matchResult?.matched || [];
        const existingBankIds = new Set();
        const existingGLIds = new Set();
        for (const m of existingMatches) {
          // Handle both single and batch match structures
          if (m.bankTxn) existingBankIds.add(m.bankTxn.id);
          if (m.bankTxns) m.bankTxns.forEach(b => existingBankIds.add(b.id));
          if (m.glEntry) existingGLIds.add(m.glEntry.id);
          if (m.glEntries) m.glEntries.forEach(g => existingGLIds.add(g.id));
        }

        // Only auto-match entries that aren't already matched
        const bankForMatching = accountData.statement.transactions.filter(t => !existingBankIds.has(t.id));
        let glForMatching = accountData.glData.entries.filter(t => !existingGLIds.has(t.id));
        // For USD accounts: filter out FX reversal entries (they're in GL but not on the statement)
        if (isUSD) {
          glForMatching = filterFXReversals(glForMatching);
        }

        const autoResult = matchTransactions(bankForMatching, glForMatching);

        // Merge existing + new auto matches
        const allMatched = [...existingMatches, ...autoResult.matched];
        const totalBank = accountData.statement.transactions.length;
        const totalGL = accountData.glData.entries.length;
        const matchedBankCount = totalBank - autoResult.unmatchedBank.length;

        const matchResult = {
          matched: allMatched,
          unmatchedBank: autoResult.unmatchedBank,
          unmatchedGL: autoResult.unmatchedGL,
          stats: {
            bankCount: totalBank,
            glCount: totalGL,
            matchedCount: matchedBankCount,
            matchRate: totalBank > 0 ? Math.round(matchedBankCount / totalBank * 100) : 0,
          },
        };

        const reconcilingItems = classifyReconcilingItems(
          matchResult.unmatchedBank,
          matchResult.unmatchedGL
        );

        // For USD: compare in USD (txn amounts)
        const glBal = isUSD && accountData.glData.txnEndingBalance !== null
          ? accountData.glData.txnEndingBalance
          : accountData.glData.endingBalance;

        const reconciliation = computeReconciliation(
          accountData.statement.metadata.closingBalance,
          glBal,
          reconcilingItems
        );

        const proposedJEs = generateProposedJEs(reconcilingItems, accountNo);

        dispatch({
          type: 'RECONCILIATION_RUN',
          accountNo,
          matchResult,
          reconcilingItems,
          reconciliation,
          proposedJEs,
        });

        // Auto-compute FX for USD accounts
        if (isUSD) {
          const fx = computeFXConversion(
            accountData.statement.metadata.closingBalance,
            fxRate,
            accountData.glData.endingBalance
          );
          dispatch({ type: 'SET_FX_CONVERSION', accountNo, fxConversion: fx });
        }

        // Save immediately after reconciliation
        setTimeout(() => saveNow?.(accountNo), 200);
      } catch (err) {
        console.error('Reconciliation failed:', err);
      } finally {
        setReconciling(false);
      }
    }, 50);
  }, [accountData, accountNo, dispatch, fxRate, isUSD]);

  // Reset account
  const handleReset = useCallback(() => {
    dispatch({ type: 'RESET_ACCOUNT', accountNo, companyId, month });
  }, [dispatch, accountNo]);

  return (
    <div style={{ padding: '12px 0' }}>
      <StepIndicator steps={steps} currentStep={stepIdx} t={t} />

      {/* Upload zones */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <BankRecUploadZone
          label="Bank Statement"
          onFileProcessed={handleStatementUpload}
          parsedResult={accountData?.statement ? { count: accountData.statement.transactions.length } : null}
          fileName={accountData?.statement?.file?.name}
        />
        <BankRecUploadZone
          label="GL Export (from Sage)"
          onFileProcessed={handleGLUpload}
          parsedResult={accountData?.glData ? { count: accountData.glData.entries.length } : null}
          fileName={accountData?.glData?.file?.name}
        />
      </div>

      {/* Column mapping fallback (when GL auto-detect fails) */}
      {glMappingNeeded && (
        <div style={{
          padding: '12px', marginBottom: 12,
          background: '#FFFDE7', border: `1px solid ${amber}`, borderRadius: 8,
        }}>
          <div style={{ fontFamily: sans, fontSize: 13, fontWeight: 600, color: t.textPrimary, marginBottom: 8 }}>
            Map GL Columns
          </div>
          <div style={{ fontFamily: sans, fontSize: 11, color: t.textSecondary, marginBottom: 10 }}>
            Auto-detection failed. Select which column contains each field.
          </div>

          {/* Preview table */}
          <div style={{ overflowX: 'auto', marginBottom: 10 }}>
            <table style={{ fontFamily: mono, fontSize: 10, borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  {(glMappingNeeded.preview[0] || []).map((_, ci) => (
                    <th key={ci} style={{ padding: '3px 6px', background: t.bgSurface, border: `1px solid ${t.border}`, textAlign: 'center', color: t.textMuted }}>
                      Col {ci}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {glMappingNeeded.preview.slice(0, 5).map((row, ri) => (
                  <tr key={ri} style={{ background: ri === glHeaderRow ? '#FEF3C7' : 'transparent' }}>
                    {row.map((cell, ci) => (
                      <td key={ci} style={{ padding: '2px 6px', border: `1px solid ${t.borderLight}`, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Header row selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontFamily: sans, fontSize: 11, color: t.textSecondary }}>Header row:</span>
            <select value={glHeaderRow} onChange={e => setGlHeaderRow(parseInt(e.target.value))}
              style={{ fontFamily: mono, fontSize: 11, padding: '2px 4px', border: `1px solid ${t.border}`, borderRadius: 3 }}>
              {glMappingNeeded.preview.map((_, i) => <option key={i} value={i}>Row {i}</option>)}
            </select>
          </div>

          {/* Column mapping dropdowns */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {[
              { key: 'date', label: 'Date', required: true },
              { key: 'description', label: 'Description' },
              { key: 'debit', label: 'Debit', required: true },
              { key: 'credit', label: 'Credit', required: true },
              { key: 'balance', label: 'Balance' },
              { key: 'txnAmount', label: 'Txn Amount (USD)' },
            ].map(({ key, label, required }) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontFamily: sans, fontSize: 11, color: t.textSecondary, minWidth: 90 }}>
                  {label}{required ? ' *' : ''}:
                </span>
                <select
                  value={glMapping[key]}
                  onChange={e => setGlMapping(prev => ({ ...prev, [key]: parseInt(e.target.value) }))}
                  style={{ fontFamily: mono, fontSize: 11, padding: '2px 4px', border: `1px solid ${t.border}`, borderRadius: 3, flex: 1 }}
                >
                  <option value={-1}>--</option>
                  {(glMappingNeeded.preview[glHeaderRow] || []).map((cell, ci) => (
                    <option key={ci} value={ci}>{ci}: {String(cell).substring(0, 20)}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 10 }}>
            <button onClick={() => setGlMappingNeeded(null)} style={{
              fontFamily: sans, fontSize: 11, padding: '4px 10px',
              border: `1px solid ${t.border}`, borderRadius: 4, background: t.bg, color: t.textSecondary, cursor: 'pointer',
            }}>Cancel</button>
            <button
              onClick={handleApplyMapping}
              disabled={glMapping.date === -1 || (glMapping.debit === -1 && glMapping.credit === -1)}
              style={{
                fontFamily: sans, fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 4,
                border: `1px solid ${glMapping.date !== -1 ? '#0D9488' : t.border}`,
                background: glMapping.date !== -1 ? '#0D9488' : t.bgSurface,
                color: glMapping.date !== -1 ? '#fff' : t.textMuted, cursor: glMapping.date !== -1 ? 'pointer' : 'not-allowed',
              }}
            >Apply Mapping</button>
          </div>
        </div>
      )}

      {/* FX Rate for USD accounts */}
      {isUSD && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
          padding: '8px 12px', background: t.bgSurface, borderRadius: 6,
          border: `1px solid ${t.border}`,
        }}>
          <span style={{ fontFamily: sans, fontSize: 12, color: t.textPrimary }}>
            Month-End FX Rate (USD/CAD):
          </span>
          <span style={{ fontFamily: mono, fontSize: 13, fontWeight: 600, color: t.textPrimary }}>
            {fxLoading ? 'Loading...' : fxRate ? fxRate.toFixed(4) : '--'}
          </span>
          {fxRateDate && (
            <span style={{ fontFamily: sans, fontSize: 10, color: t.textMuted }}>
              Bank of Canada -- {fxRateDate}
            </span>
          )}
        </div>
      )}

      {/* Reconcile button */}
      {accountData?.statement && accountData?.glData && (
        <button
          onClick={handleReconcile}
          disabled={reconciling}
          style={{
            fontFamily: sans, fontSize: 13, fontWeight: 600,
            padding: '10px 20px', borderRadius: 6, width: '100%',
            border: 'none',
            background: reconciling ? t.textMuted : teal,
            color: '#fff', cursor: reconciling ? 'wait' : 'pointer',
            marginBottom: 16,
          }}
        >
          {reconciling ? 'Reconciling...' : 'Reconcile'}
        </button>
      )}

      {/* Match results */}
      {accountData?.matchResult && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: sans, fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 8 }}>
            Transaction Matching
          </div>
          <BankRecMatchView
            matchResult={accountData.matchResult}
            bankAccountNo={accountNo}
            glAccounts={glAccounts}
            onFlipSign={(txnId, source) => dispatch({ type: 'FLIP_SIGN', accountNo, txnId, source })}
            onManualMatch={(bankItems, glItems) => {
              dispatch({
                type: 'MANUAL_MATCH',
                accountNo,
                bankItems,
                glItems,
              });
              setTimeout(() => saveNow?.(accountNo), 200);
            }}
            onCreateJE={(je) => {
              dispatch({
                type: 'ADD_PROPOSED_JE',
                accountNo,
                je: { ...je, id: `je-manual-${Date.now()}`, approved: false },
              });
            }}
          />
        </div>
      )}

      {/* FX Conversion section (USD) */}
      {isUSD && accountData?.fxConversion && (
        <div style={{
          background: t.bgSurface, borderRadius: 8, padding: 14,
          border: `1px solid ${t.border}`, marginBottom: 12,
        }}>
          <div style={{ fontFamily: sans, fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 8 }}>
            FX Conversion to CAD
          </div>
          {[
            ['USD Ending Balance (per bank)', accountData.fxConversion.usdEnding],
            ['Month-End Rate (USD/CAD)', accountData.fxConversion.fxRate],
            ['Converted CAD Balance', accountData.fxConversion.convertedCAD],
            ['GL Ending Balance (CAD per GL)', accountData.fxConversion.glCADEnding],
          ].map(([label, val]) => (
            <div key={label} style={{
              display: 'flex', justifyContent: 'space-between', padding: '3px 0',
              borderBottom: `1px solid ${t.borderLight}`,
            }}>
              <span style={{ fontFamily: sans, fontSize: 12, color: t.textPrimary }}>{label}</span>
              <span style={{ fontFamily: mono, fontSize: 12, color: t.textPrimary }}>
                {typeof val === 'number' ? val.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : val}
              </span>
            </div>
          ))}
          <div style={{
            display: 'flex', justifyContent: 'space-between', padding: '6px 0',
            fontWeight: 700,
          }}>
            <span style={{ fontFamily: sans, fontSize: 12, color: t.textPrimary }}>FX JOURNAL ENTRY (CAD)</span>
            <span style={{ fontFamily: mono, fontSize: 12, color: accountData.fxConversion.fxJEAmount < 0 ? red : green }}>
              {accountData.fxConversion.fxJEAmount.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      )}

      {/* Discrepancies & Proposed JEs */}
      {liveReconciliation && (
        <BankRecDiscrepancies
          reconciliation={liveReconciliation}
          reconcilingItems={liveReconcilingItems}
          proposedJEs={accountData.proposedJEs || []}
          currency={accountConfig?.currency}
          onApproveJE={(jeId, approved) => dispatch({ type: 'APPROVE_JE', accountNo, jeId, approved })}
          onApproveAllJEs={() => dispatch({ type: 'APPROVE_ALL_JES', accountNo })}
        />
      )}

      {/* Footer actions */}
      {status !== 'not_started' && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, paddingTop: 12, borderTop: `1px solid ${t.border}` }}>
          <button
            onClick={handleReset}
            style={{
              fontFamily: sans, fontSize: 11, padding: '5px 12px',
              border: `1px solid ${t.border}`, borderRadius: 4,
              background: t.bg, color: t.textSecondary, cursor: 'pointer',
            }}
          >
            Reset
          </button>
          {status === 'matched' && (
            <button
              onClick={() => { dispatch({ type: 'MARK_COMPLETED', accountNo }); setTimeout(() => saveNow?.(accountNo), 100); }}
              style={{
                fontFamily: sans, fontSize: 12, fontWeight: 600,
                padding: '6px 16px', borderRadius: 4,
                border: `1px solid ${green}`, background: greenLight,
                color: greenDark, cursor: 'pointer',
              }}
            >
              Mark Complete
            </button>
          )}
        </div>
      )}
    </div>
  );
}
